<template>
  <div
    ref="wrapper"
    class="video-popup__wrapper"
    @click.self="hideVideo">
    <div
      ref="videoPopup"
      class="video-popup">
      <canvas
        ref="videoCanvas"
        type="video/mp4"
        class="video-popup__video" />
      <div
        v-if="isStopped"
        class="video-popup__poster" />
      <div
        v-if="video"
        class="video-popup__gradient video-popup__controls"
        :class="{'video-popup__controls--shown': mouseMove || isPaused || isStopped}"
        @click="togglePlayState">
        <div class="video-popup__bottom-panel">
          <div class="video-popup__title">
            How we try hard
          </div>
          <div class="video-popup__subtitle">
            Watch the video about SkyCoach
          </div>
          <div class="video-popup__timeline">
            <div
              class="video-popup__timeline-bar"
              :style="{'--position': `${position / duration * 100}%`}"
              @click.stop="seek" />
            <span class="video-popup__timeline-text">
              {{ Math.floor(position / 60) }}:{{ Math.round(position % 60).toString().padStart(2, '0') }} /
              {{ Math.floor(duration / 60) }}:{{ Math.round(duration % 60).toString().padStart(2, '0') }}
            </span>
          </div>
        </div>
        <div
          class="video-popup__close"
          @click.stop="hideVideo">
          <div class="shape-cross" />
        </div>
        <video-pulse-play
          v-if="isPaused || isStopped"
          class="video-popup__play" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import VideoPulsePlay from '../VideoAbout/VideoPulsePlay.vue';

export default {
  name: 'PromoVideoPopup',

  components: { VideoPulsePlay },

  data() {
    return {
      controlsTimer: null,
      positionInterval: null,
      mouseMove: true,
      video: null,
      duration: 0,
      position: 0,
      isStopped: true,
      isPaused: false,
    };
  },

  computed: {
    ...mapState(['userDevice']),
  },

  mounted() {
    const canvas = this.$refs.videoCanvas;
    const ctx = canvas.getContext('2d');

    this.video = document.createElement('video');
    canvas.height = window.innerHeight;
    canvas.width = (window.innerHeight / 9) * 16;

    this.video.addEventListener('play', () => {
      const step = () => {
        if (!this.video || !canvas) {
          return;
        }

        ctx.drawImage(this.video, 0, 0, canvas.width, canvas.height);
        requestAnimationFrame(step);
      };

      this.isStopped = false;
      this.isPaused = false;
      this.positionInterval = setInterval(() => {
        this.position = this.video.currentTime;
      }, 100);
      requestAnimationFrame(step);
    });

    this.video.addEventListener('loadedmetadata', () => {
      this.duration = this.video.duration;
    });

    this.video.addEventListener('pause', () => {
      this.isPaused = true;
      clearInterval(this.positionInterval);
    });

    this.video.addEventListener('ended', () => {
      this.isStopped = true;
    });

    this.$refs.videoPopup.addEventListener('mousemove', this.showControls);
    this.video.src = '/videos/promo.mp4';
  },

  beforeDestroy() {
    clearInterval(this.positionInterval);
    this.$refs.videoPopup.removeEventListener('mousemove', this.showControls);
    this.video.pause();
    this.video = null;
  },

  methods: {
    ...mapActions(['hideVideo']),

    showControls() {
      this.mouseMove = true;
      clearTimeout(this.controlsTimer);
      this.controlsTimer = setTimeout(() => {
        this.mouseMove = false;
        clearTimeout(this.controlsTimer);
      }, 1000);
    },

    togglePlayState() {
      if (this.isPaused || this.isStopped) {
        this.video.play();
      } else {
        this.video.pause();
      }
    },

    seek(e) {
      const position = e.offsetX / e.target.getBoundingClientRect().width;
      this.video.currentTime = position * this.duration;
      this.position = this.video.currentTime;
    },
  },
};
</script>

<style>
  /* фоллбэк backdrop-filter для firefox, в остальных браузерах
   будет двойное размытие, но это почти не заметно */
  .video-popup__wrapper ~ .root__app,
  .video-popup__wrapper ~ .header {
    filter: blur(5px);
  }
</style>

<style lang="scss" scoped>
  .video-popup__wrapper {
    position: fixed;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    background-color: $color--basic-black;
    z-index: 2147483004;
  }

  .video-popup {
    height: 100%;
    width: 100%;
  }

  .video-popup__video {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: contain;
    left: 0;
    top: 50%;
    pointer-events: none;
    transform: translateY(-50%);
  }

  .video-popup__gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba($color--basic-black, 0) 0%, $color--basic-black 100%);
  }

  .video-popup__controls {
    opacity: 0;
    transition: opacity var(--transition-opacity-time) var(--transition-opacity-easing);
    cursor: pointer;
  }

  .video-popup__controls--shown {
    opacity: 1;
  }

  .video-popup__bottom-panel {
    position: absolute;
    left: 40px;
    bottom: 50px;
    width: calc(100% - 80px);
  }

  .video-popup__title {
    font-size: 19px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .video-popup__subtitle {
    font-size: 13px;
    margin-bottom: 16px;
  }

  .video-popup__timeline {
    display: grid;
    grid-template-columns: 1fr 80px;
    align-items: center;
  }

  .video-popup__timeline-text {
    font-size: 13px;
    text-align: right;
  }

  .video-popup__timeline-bar {
    position: relative;
    width: 100%;
    height: 4px;
    border-radius: 100px;
    background-color: $color--basic-black;
    overflow: hidden;
    transition: transform 0.15s ease-out;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: var(--position);
      border-radius: 100px;
      background-color: $color--basic-blue;
    }

    &:hover {
      transform: scaleY(1.5);
    }
  }

  .video-popup__close {
    position: absolute;
    top: 0;
    right: 0;
    height: 60px;
    width: 60px;
    padding: 20px;

    .shape-cross {
      transition: transform 0.15s ease-out;
    }

    &:hover .shape-cross {
      transform: scale(1.2);
    }
  }

  .video-popup__play {
    position: absolute;
    top: 50%;
    left: 50%;
    height: 40px;
    width: 40px;
    transform: translate(-50%, -50%);
    transition: transform 0.15s ease-out;

    &:hover {
      transform: translate(-50%, -50%) scale(1.2);
    }
  }

  .video-popup__poster {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-image: url(/images/bg/video_poster/video_poster_600.jpg);
    background-size: cover;
    background-position: center;
  }

  @media screen and (min-width: 600px) {
    .video-popup__poster {
      background-image: url(/images/bg/video_poster/video_poster_1024.jpg);
    }
  }

  @media screen and #{$screen-more-or-1024} {
    .video-popup {
      position: relative;
      max-width: 50%;
      height: auto;
      border-radius: 15px;
      overflow: hidden;
    }

    .video-popup__video {
      position: relative;
      transform: unset;
      object-fit: contain;
    }

    .video-popup__wrapper {
      background-color: transparent;
      display: grid;
      place-items: center;
      backdrop-filter: blur(5px);
    }

    .video-popup__poster {
      background-image: url(/images/bg/video_poster/video_poster_2544.jpg);
    }
  }
</style>
